import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bookmark,
  Check,
  Database,
  Filter,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  Table2,
  Trash2,
  X,
} from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { useRedirect } from '../../../Shared/Hooks/useRedirect ';
import { dataServiceBase } from './dataServiceRoutes';
import {
  getConnectionTables,
  getDataConnections,
  getTableColumns,
  mutateRow,
  runDataQuery,
} from '../../Services/DataService';
import {
  ColumnMeta,
  CONTROL_KIND_HINTS,
  CONTROL_KIND_LABELS,
  CONTROL_KIND_STYLES,
  DataConnection,
  DataQueryRequest,
  DataQueryResult,
  FILTER_OPERATOR_OPTIONS,
  FilterCondition,
  FilterOperator,
  LIST_OPERATORS,
  NO_VALUE_OPERATORS,
  RANGE_OPERATORS,
  ENVIRONMENT_STYLES,
  UNKNOWN_COLUMN_STYLE,
} from '../../Types/DataServiceType';
import DataResultGrid from './DataResultGrid';
import SearchableSelect, { SearchableOption } from './SearchableSelect';
import SuggestedValueInput from './SuggestedValueInput';
import RowEditorModal from './RowEditorModal';
import { primaryKeyLabelsFor } from './recordLink';

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-xs text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-500';

const emptyFilter = (column = ''): FilterCondition => ({
  column,
  operator: 'EQ',
  value: '',
});

const QueryBuilderComponent = () => {
  const redirect = useRedirect();

  // Resolved from the role table rather than inline, so a configured recruiter
  // or candidate lands on the saved-query page they are actually allowed to open.
  const savedQueriesBase = dataServiceBase();

  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [tables, setTables] = useState<string[]>([]);
  const [tableName, setTableName] = useState('');
  const [columns, setColumns] = useState<ColumnMeta[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [columnFilter, setColumnFilter] = useState('');
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [filterJoin, setFilterJoin] = useState<'AND' | 'OR'>('AND');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [limit, setLimit] = useState('100');
  const [offset, setOffset] = useState('0');

  const [loadingConnections, setLoadingConnections] = useState(true);
  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<DataQueryResult | null>(null);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [rowModalOpen, setRowModalOpen] = useState(false);
  const [rowOperation, setRowOperation] = useState<'INSERT' | 'UPDATE' | 'DELETE'>('INSERT');
  const [rowTarget, setRowTarget] = useState<Record<string, unknown> | null>(null);

  const connection = useMemo(
    () => connections.find((item) => item.id === connectionId) ?? null,
    [connections, connectionId]
  );

  const tableOptions = useMemo<SearchableOption[]>(
    () => tables.map((table) => ({ value: table, label: table })),
    [tables]
  );

  // Every column of the selected table, tagged with its type so the chip colour
  // says what the column is before anyone reads the name.
  const columnOptions = useMemo<SearchableOption[]>(
    () =>
      columns.map((column) => ({
        value: column.columnName,
        label: column.columnName,
        hint: column.controlKind ? CONTROL_KIND_LABELS[column.controlKind] : undefined,
        keywords: `${column.dataType ?? ''} ${column.typeName ?? ''} ${column.columnType ?? ''} ${
          column.primaryKey ? 'primary key pk' : ''
        }`,
      })),
    [columns]
  );

  const visibleColumns = useMemo(() => {
    const needle = columnFilter.trim().toLowerCase();
    if (!needle) return columns;
    return columns.filter((column) =>
      `${column.columnName} ${column.dataType ?? ''} ${column.typeName ?? ''} ${column.columnType ?? ''} ${
        column.primaryKey ? 'primary key pk' : ''
      }`
        .toLowerCase()
        .includes(needle)
    );
  }, [columns, columnFilter]);

  /* -------------------------------------------------------------- loaders */

  const loadConnections = useCallback(async () => {
    setLoadingConnections(true);
    try {
      const response = await getDataConnections({}, 0, 100);
      if (response.success && response.data) {
        const items = response.data.page?.content ?? [];
        setConnections(items);
        setConnectionId((current) => {
          if (current && items.some((item) => item.id === current)) return current;
          return items[0]?.id ?? '';
        });
      }
    } catch (err) {
      console.error('Failed to load connections', err);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const loadTables = useCallback(async () => {
    if (!connectionId) {
      setTables([]);
      return;
    }
    setLoadingTables(true);
    try {
      const response = await getConnectionTables(connectionId, false);
      if (response.success && response.data) {
        setTables((response.data.tables ?? []).map((table) => table.tableName));
      } else {
        setTables([]);
        setError(response.message || 'Could not read the table list.');
      }
    } catch (err) {
      console.error('Failed to load tables', err);
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  }, [connectionId]);

  useEffect(() => {
    loadTables();
    setTableName('');
    setColumns([]);
    setSelectedColumns([]);
    setFilters([]);
    setResult(null);
    setError('');
  }, [loadTables, connectionId]);

  const loadColumns = useCallback(async () => {
    if (!connectionId || !tableName) {
      setColumns([]);
      return;
    }
    setLoadingColumns(true);
    try {
      const response = await getTableColumns(connectionId, tableName);
      if (response.success && response.data) {
        setColumns(response.data);
        setSelectedColumns([]);
        setFilters([]);
      } else {
        setColumns([]);
        setError(response.message || 'Could not read the column list.');
      }
    } catch (err) {
      console.error('Failed to load columns', err);
      setColumns([]);
    } finally {
      setLoadingColumns(false);
    }
  }, [connectionId, tableName]);

  useEffect(() => {
    loadColumns();
  }, [loadColumns]);

  /* --------------------------------------------------------------- filters */

  const setFilter = <K extends keyof FilterCondition>(index: number, key: K, value: FilterCondition[K]) => {
    setFilters((prev) => prev.map((filter, i) => (i === index ? { ...filter, [key]: value } : filter)));
  };

  const addFilter = () => setFilters((prev) => [...prev, emptyFilter()]);

  const removeFilter = (index: number) =>
    setFilters((prev) => prev.filter((_, i) => i !== index));

  /* ------------------------------------------------------------------ run */

  const buildRequest = (): DataQueryRequest | string => {
    if (!connectionId) return 'Pick a connection first.';
    if (!tableName) return 'Pick a table first.';

    const limitValue = Number(limit);
    if (!Number.isInteger(limitValue) || limitValue < 1 || limitValue > 50000) {
      return 'The limit must be a whole number between 1 and 50000.';
    }

    const offsetValue = Number(offset);
    if (!Number.isInteger(offsetValue) || offsetValue < 0) {
      return 'The offset cannot be negative.';
    }

    for (let i = 0; i < filters.length; i += 1) {
      const filter = filters[i];
      if (!filter.column) return `Filter ${i + 1} is missing a column.`;

      const operator = filter.operator;
      if (NO_VALUE_OPERATORS.includes(operator)) continue;

      if (LIST_OPERATORS.includes(operator)) {
        const values = (filter.values ?? []).filter((value) => String(value).trim() !== '');
        if (values.length === 0) return `Filter ${i + 1} needs at least one value.`;
        if (values.length > 100) return `Filter ${i + 1} cannot hold more than 100 values.`;
        continue;
      }

      if (filter.value === undefined || String(filter.value).trim() === '') {
        return `Filter ${i + 1} needs a value.`;
      }

      if (RANGE_OPERATORS.includes(operator)) {
        if (!filter.valueTo || String(filter.valueTo).trim() === '') {
          return `Filter ${i + 1} needs a second value for Between.`;
        }
      }
    }

    if (sortColumn && !columns.some((column) => column.columnName === sortColumn)) {
      return 'The sort column is not on this table.';
    }

    return {
      connectionId,
      tableName,
      filters: filters.map((filter) => {
        if (NO_VALUE_OPERATORS.includes(filter.operator)) {
          return { column: filter.column, operator: filter.operator };
        }
        if (LIST_OPERATORS.includes(filter.operator)) {
          return {
            column: filter.column,
            operator: filter.operator,
            values: (filter.values ?? []).filter((value) => String(value).trim() !== ''),
          };
        }
        if (RANGE_OPERATORS.includes(filter.operator)) {
          return {
            column: filter.column,
            operator: filter.operator,
            value: filter.value,
            valueTo: filter.valueTo,
          };
        }
        return { column: filter.column, operator: filter.operator, value: filter.value };
      }),
      filterJoin,
      limit: limitValue,
      offset: offsetValue,
      ...(sortColumn ? { sortColumn, sortDirection } : {}),
      ...(selectedColumns.length > 0 ? { columns: selectedColumns } : {}),
    };
  };

  /**
   * Turns a result row into the href of its record page.
   *
   * <p>Returns null unless the result set actually carries a primary key. A
   * projection that dropped the key columns, or a result with no key at all,
   * has no single record behind it and so must not pretend to be clickable.</p>
   */
  const primaryKeyLabels = useMemo(
    () => primaryKeyLabelsFor(columns, result?.columns ?? []),
    [columns, result]
  );

  const recordLink = useMemo(
    () => ({ connectionId, table: tableName, primaryKeyLabels }),
    [connectionId, tableName, primaryKeyLabels]
  );

  const handleRun = async () => {
    const request = buildRequest();
    if (typeof request === 'string') {
      setError(request);
      return;
    }

    setRunning(true);
    setError('');
    setResult(null);

    try {
      const response = await runDataQuery(request);
      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setError(response.message || 'The query could not be run.');
      }
    } catch (err) {
      console.error('Query failed', err);
      setError('The query could not be run.');
    } finally {
      setRunning(false);
    }
  };

  const saveAsQuery = () => {
    if (!connectionId || !tableName) {
      setAlert({
        variant: 'warning',
        title: 'Nothing to save',
        message: 'Pick a connection and a table before saving this as a query.',
        autoCloseMs: 4000,
        onClose: () => setAlert(null),
      });
      return;
    }
    // Filters and sort are handed to the saved-query page through the URL so
    // the builder state survives the navigation without a global store.
    const payload = {
      connectionId,
      tableName,
      filtersJson: JSON.stringify(filters),
      sortJson: JSON.stringify({ sortColumn, sortDirection }),
      limitValue: Number(limit) || 100,
    };
    redirect(`${savedQueriesBase}?draft=${encodeURIComponent(JSON.stringify(payload))}`);
  };

  const isProduction = connection?.environment === 'PRODUCTION';

  // Writes are only offered when the connection's own ceiling allows them;
  // the server re-checks this on every request.
  const canWrite = connection?.effectivePermission === 'READ_WRITE';
  const primaryKeys = useMemo(
    () => columns.filter((column) => column.primaryKey).map((column) => column.columnName),
    [columns]
  );

  const openRowModal = (operation: 'INSERT' | 'UPDATE' | 'DELETE', row: Record<string, unknown> | null) => {
    setRowOperation(operation);
    setRowTarget(row);
    setRowModalOpen(true);
  };

  const applyRowChange = async (request: {
    key: Record<string, unknown>;
    values: Record<string, unknown>;
    confirmProductionAlias?: string;
  }) => {
    if (!connectionId || !tableName) return;

    const response = await mutateRow({
      connectionId,
      tableName,
      operation: rowOperation,
      key: Object.keys(request.key).length > 0 ? request.key : undefined,
      values: Object.keys(request.values).length > 0 ? request.values : undefined,
      ...(request.confirmProductionAlias
        ? { confirmProductionAlias: request.confirmProductionAlias }
        : {}),
    });

    if (response.success) {
      setAlert({
        variant: 'success',
        title: 'Applied',
        message:
          rowOperation === 'DELETE'
            ? 'The row was deleted.'
            : rowOperation === 'INSERT'
            ? 'The row was inserted.'
            : 'The row was updated.',
        autoCloseMs: 4000,
        onClose: () => setAlert(null),
      });
      setRowModalOpen(false);
      await handleRun();
    } else {
      throw new Error(response.message || 'The change could not be applied.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Filter size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Query Builder</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Structured reads. Every value is bound, never interpolated
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={loadConnections}
            disabled={loadingConnections}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loadingConnections ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={saveAsQuery}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:border-cyan-500/40"
          >
            <Save size={15} /> Save as query
          </button>
          <button
            onClick={handleRun}
            disabled={running}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
            {running ? 'Running' : 'Run query'}
          </button>
        </div>
      </div>

      {isProduction && (
        <div className="mb-4 flex items-center gap-2 rounded-2xl border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-xs font-bold text-rose-300">
          <AlertTriangle size={15} />
          <span className="font-black uppercase tracking-widest">Production</span>
          <span className="text-slate-400">
            Reads are unrestricted, but any row change needs the alias typed out.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Source */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
          <p className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
            <Database size={14} /> Source
          </p>

          <div className="space-y-3">
            <div>
              <label className={labelClass}>Connection</label>
              <select
                value={connectionId}
                onChange={(e) => setConnectionId(e.target.value)}
                disabled={loadingConnections}
                className={fieldClass}
              >
                <option value="" className="bg-slate-900 text-slate-100">
                  {loadingConnections ? 'Loading…' : 'Select a connection'}
                </option>
                {connections.map((item) => (
                  <option key={item.id} value={item.id} className="bg-slate-900 text-slate-100">
                    {item.alias} ({item.environment})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Table</label>
              <SearchableSelect
                options={tableOptions}
                value={tableName}
                onChange={setTableName}
                disabled={!connectionId || loadingTables}
                loading={loadingTables}
                placeholder="Search tables…"
                emptyLabel={
                  loadingTables
                    ? 'Loading…'
                    : !connectionId
                    ? 'Pick a connection first'
                    : tables.length === 0
                    ? 'No tables visible'
                    : 'Select a table'
                }
              />
            </div>

            <div>
              <label className={labelClass}>Sort column</label>
              <SearchableSelect
                options={columnOptions}
                value={sortColumn}
                onChange={setSortColumn}
                disabled={!tableName || loadingColumns}
                loading={loadingColumns}
                placeholder="Search columns…"
                emptyLabel={!tableName ? 'Pick a table first' : 'Unsorted'}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Direction</label>
                <select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value as 'ASC' | 'DESC')}
                  disabled={!sortColumn}
                  className={fieldClass}
                >
                  <option value="ASC" className="bg-slate-900 text-slate-100">
                    Ascending
                  </option>
                  <option value="DESC" className="bg-slate-900 text-slate-100">
                    Descending
                  </option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Limit</label>
                <input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  className={fieldClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Offset</label>
              <input
                type="number"
                value={offset}
                onChange={(e) => setOffset(e.target.value)}
                className={fieldClass}
              />
            </div>
          </div>
        </div>

        {/* Columns and filters */}
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                <Table2 size={14} /> Columns
                {columns.length > 0 && (
                  <span className="text-slate-600">
                    ({selectedColumns.length === 0 ? 'all' : `${selectedColumns.length} of ${columns.length}`})
                  </span>
                )}
              </p>
              {columns.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedColumns([])}
                    className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest transition ${
                      selectedColumns.length === 0
                        ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                        : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setSelectedColumns(columns.map((column) => column.columnName))}
                    className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest transition ${
                      selectedColumns.length === columns.length
                        ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                        : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Every column
                  </button>
                </div>
              )}
            </div>

            {loadingColumns ? (
              <div className="space-y-2">
                {[0, 1, 2].map((idx) => (
                  <div key={idx} className="h-8 w-full animate-pulse rounded-lg bg-slate-800/60" />
                ))}
              </div>
            ) : columns.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-700 py-6 text-center text-[11px] font-bold text-slate-500">
                Pick a table to load its columns.
              </p>
            ) : (
              <>
                {/* Narrow the column list before picking from a wide table. */}
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    value={columnFilter}
                    onChange={(event) => setColumnFilter(event.target.value)}
                    placeholder="Search columns by name or type…"
                    className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2 pl-9 pr-8 text-xs text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
                  />
                  {columnFilter && (
                    <button
                      onClick={() => setColumnFilter('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-300"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>

                {visibleColumns.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-slate-700 py-6 text-center text-[11px] font-bold text-slate-500">
                    No column matches “{columnFilter}”.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {visibleColumns.map((column) => {
                      const on = selectedColumns.includes(column.columnName);
                      // Colour comes from the column type, so the whole list can
                      // be scanned without reading a single name.
                      const typeStyle = column.controlKind
                        ? CONTROL_KIND_STYLES[column.controlKind]
                        : UNKNOWN_COLUMN_STYLE;
                      return (
                        <button
                          key={column.columnName}
                          onClick={() =>
                            setSelectedColumns((prev) =>
                              prev.includes(column.columnName)
                                ? prev.filter((name) => name !== column.columnName)
                                : [...prev, column.columnName]
                            )
                          }
                          title={`${column.columnName} · ${column.columnType ?? column.dataType ?? 'unknown type'}${
                            column.nullable ? '' : ' · not null'
                          }`}
                          className={`group inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest transition ${
                            on ? `${typeStyle} ring-1 ring-cyan-500/50` : 'border-slate-800 bg-slate-950 text-slate-500 hover:text-slate-300'
                          }`}
                        >
                          {on && <Check size={11} className="flex-shrink-0" />}
                          {column.columnName}
                          {column.primaryKey && <span className="text-cyan-500">•</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {visibleColumns.length < columns.length && (
                  <p className="mt-2 text-[10px] font-semibold text-slate-500">
                    Showing {visibleColumns.length} of {columns.length} columns.
                  </p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-800 pt-3 text-[9px] font-black uppercase tracking-widest text-slate-600">
                  {([
                    'TEXT', 'NUMBER', 'DATE', 'DATETIME', 'BOOLEAN', 'ENUM', 'JSON', 'UUID', 'BINARY',
                  ] as const).map((kind) => (
                    <span key={kind} className="flex items-center gap-1">
                      <span
                        className={`h-2 w-2 rounded-full border ${CONTROL_KIND_STYLES[kind].split(' ')[0]}`}
                      />
                      {CONTROL_KIND_LABELS[kind]}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                <Filter size={14} /> Filters
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={filterJoin}
                  onChange={(e) => setFilterJoin(e.target.value as 'AND' | 'OR')}
                  className={`${fieldClass} w-24`}
                >
                  <option value="AND" className="bg-slate-900 text-slate-100">
                    AND
                  </option>
                  <option value="OR" className="bg-slate-900 text-slate-100">
                    OR
                  </option>
                </select>
                <button
                  onClick={addFilter}
                  disabled={!tableName}
                  className="flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
                >
                  <Plus size={12} /> Add
                </button>
              </div>
            </div>

            {filters.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-700 py-6 text-center text-[11px] font-bold text-slate-500">
                No filters. The whole table will be read, up to the limit.
              </p>
            ) : (
              <div className="space-y-2">
                {filters.map((filter, index) => {
                  const meta = columns.find((column) => column.columnName === filter.column);
                  const operator = filter.operator;
                  const isList = LIST_OPERATORS.includes(operator);
                  const isRange = RANGE_OPERATORS.includes(operator);
                  const needsValue = !NO_VALUE_OPERATORS.includes(operator);
                  const hint = meta?.controlKind ? CONTROL_KIND_HINTS[meta.controlKind] : undefined;

                  return (
                    <div key={index} className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                        <div className="md:col-span-4">
                          <label className={labelClass}>Column</label>
                          <SearchableSelect
                            options={columnOptions}
                            value={filter.column}
                            onChange={(value) => setFilter(index, 'column', value)}
                            placeholder="Search columns…"
                            emptyLabel="Select"
                          />
                        </div>

                        <div className="md:col-span-3">
                          <label className={labelClass}>Operator</label>
                          <select
                            value={operator}
                            onChange={(e) => setFilter(index, 'operator', e.target.value as FilterOperator)}
                            className={fieldClass}
                          >
                            {FILTER_OPERATOR_OPTIONS.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                className="bg-slate-900 text-slate-100"
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {needsValue && (
                          <div className="md:col-span-4">
                            <label className={labelClass}>
                              {isList ? 'Values (comma separated)' : isRange ? 'From' : 'Value'}
                            </label>
                            <SuggestedValueInput
                              connectionId={connectionId}
                              tableName={tableName}
                              columnName={filter.column}
                              allowed={
                                Boolean(meta?.suggestionAllowed) && !isList && !isRange
                              }
                              value={isList ? (filter.values ?? []).join(', ') : filter.value ?? ''}
                              onChange={(value) =>
                                setFilter(
                                  index,
                                  isList ? 'values' : 'value',
                                  isList ? value.split(',').map((part) => part.trim()).filter(Boolean) : value
                                )
                              }
                              placeholder={isList ? 'a, b, c' : 'Value'}
                            />
                          </div>
                        )}

                        {isRange && (
                          <div className="md:col-span-4">
                            <label className={labelClass}>To</label>
                            <input
                              type="text"
                              value={filter.valueTo ?? ''}
                              onChange={(e) => setFilter(index, 'valueTo', e.target.value)}
                              className={fieldClass}
                            />
                          </div>
                        )}

                        <div className="flex items-end md:col-span-1">
                          <button
                            onClick={() => removeFilter(index)}
                            className="w-full rounded-lg border border-slate-800 bg-slate-950 px-2 py-2 text-rose-400 transition hover:border-rose-500/40"
                            title="Remove filter"
                          >
                            <Trash2 size={14} className="mx-auto" />
                          </button>
                        </div>
                      </div>

                      {hint && (
                        <p className="mt-2 text-[10px] font-semibold text-slate-500">{hint}</p>
                      )}

                      {meta?.enumValues && meta.enumValues.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {meta.enumValues.map((value) => (
                            <button
                              key={value}
                              onClick={() => setFilter(index, 'value', value)}
                              className="rounded-full border border-slate-800 bg-slate-950 px-2 py-0.5 text-[10px] font-bold text-slate-400 hover:text-cyan-400"
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-black tracking-tight text-slate-100">
          Results
          {result && <span className="text-[11px] font-bold text-slate-500">{result.rowCount} rows</span>}
        </h2>

        {canWrite && tableName && (
          <button
            onClick={() => openRowModal('INSERT', null)}
            className="flex items-center gap-1.5 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-xs font-black uppercase tracking-widest text-cyan-300 transition hover:bg-cyan-500/20"
          >
            <Plus size={14} /> Insert row
          </button>
        )}
      </div>

      <div className="mt-3">
        <DataResultGrid
          result={result}
          loading={running}
          error={error}
          canWrite={canWrite && tableName.length > 0}
          onRowEdit={canWrite ? (row) => openRowModal('UPDATE', row) : undefined}
          onRowDelete={canWrite ? (row) => openRowModal('DELETE', row) : undefined}
          recordLink={recordLink}
          emptyMessage={
            tableName
              ? 'Filters are set. Run the query to see rows.'
              : 'Pick a connection and a table to start.'
          }
        />
      </div>

      {connection && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] font-bold text-slate-500">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
              ENVIRONMENT_STYLES[connection.environment] ?? 'border-slate-700 bg-slate-950 text-slate-400'
            }`}
          >
            {connection.environment}
          </span>
          <span>Row cap {connection.rowCap.toLocaleString()}</span>
          <span>Query timeout {connection.queryTimeoutSec}s</span>
          {!canWrite && <span className="text-slate-600">Read only, no row changes</span>}
          {selectedColumns.length > 0 && <span>{selectedColumns.length} columns selected</span>}
          {result?.executedSql && (
            <span className="flex items-center gap-1 text-cyan-500">
              <Bookmark size={12} /> {result.durationMs} ms
            </span>
          )}
          {tableName && (
            <button
              onClick={() => {
                setFilters([]);
                setResult(null);
                setError('');
              }}
              className="ml-auto flex items-center gap-1.5 text-rose-400 hover:underline"
            >
              <X size={12} /> Clear filters
            </button>
          )}
        </div>
      )}

      <RowEditorModal
        isOpen={rowModalOpen}
        operation={rowOperation}
        tableName={tableName}
        alias={connection?.alias ?? ''}
        isProduction={isProduction}
        primaryKeys={primaryKeys}
        columns={columns}
        row={rowTarget}
        onCancel={() => setRowModalOpen(false)}
        onConfirm={applyRowChange}
      />
    </div>
  );
};

export default QueryBuilderComponent;
